<template>
  <div class="role-design-page"> 
    <div class="canvas-wrapper">
        <div id="role-design-sandbox-container"></div>
    </div>
    <!-- Talent Card Preview Component, controlled by events -->
    <TalentCardPreview
        v-if="isTalentModalOpen"
        ref="talentCardRef"
        :userId="selectedUserId"
        :userData="selectedUserData"
    />
    <RoleInspectorPopup ref="roleInspector" />
  </div>
</template>

<script>
import { SandboxView } from './components/SandboxView.js'; 
import Utils from '@/views/org-dashboard/users/components/OrganiGram/src/utility.js';
import { app } from '@/views/org-dashboard/users/components/OrganiGram/src/feathers-socketio.js';
import TalentCardPreview from '@/views/org-dashboard/talent-pool/components/TalentCardPreview.vue';
import RoleInspectorPopup from './components/RoleInspectorPopup.vue';

export default {
  name: 'RoleDesign',
  components: {
      TalentCardPreview,
      RoleInspectorPopup
  },
  data() {
    return {
      sandboxInstance: null,
      graphData: [],
      orgName: "Loading...",
      rootId: null,
      primaryColor: "#48A9C5",
      kvdrId: null,
      isInitializing: false,
      isTalentModalOpen: false,
      selectedUserId: "",
      selectedUserData: null,
    }
  },
  computed: {
    orgData() {
        return this.$store.state.organization.orgData || {};
    },
    userId() {
        return this.$store.state.auth?.user?._id || null;
    }
  },
  watch: {
    orgData: {
        deep: true,
        handler(newVal) {
            if (newVal && newVal._id && !this.sandboxInstance && !this.isInitializing) {
                this.fetchDataAndInit();
            }
        }
    }
  },
  async mounted() {
    if (this.orgData && this.orgData._id && !this.sandboxInstance) {
        await this.fetchDataAndInit();
    } else {
        const slug = this.$route.params.slug;
        if (slug) {
            try {
                await this.$store.dispatch('organization/fetchOrganizationBySlug', slug);
            } catch (e) {
                console.warn("Could not manually fetch org by slug. Waiting for watcher.", e);
            }
        }
    }
    // Event listeners to bridge vanilla JS and Vue
    window.addEventListener('show-talent-card', this.handleShowTalentCard);
    window.addEventListener('show-role-inspector', this.handleShowRoleInspector);
  },
  beforeDestroy() {
    if (this.sandboxInstance) {
      this.sandboxInstance.destroy();
    }
    window.removeEventListener('show-talent-card', this.handleShowTalentCard);
    window.removeEventListener('show-role-inspector', this.handleShowRoleInspector);
  },
  methods: {
    async handleShowTalentCard(event) {
      const userId = event.detail.userId;
      if (!userId) return;

      this.$vs.loading();
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const response = await fetch(`https://api.getboarded.tech/users/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await response.json();
        
        this.selectedUserId = userId;
        this.selectedUserData = json;
        this.isTalentModalOpen = true;

        // Trigger the popup inside the talent card component
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.talentCardRef) {
               // This triggers the internal vs-popup of the talent card
               const trigger = this.$refs.talentCardRef.$el.querySelector('i') || this.$refs.talentCardRef.$el;
               if (trigger && trigger.click) trigger.click();
            }
          }, 100);
        });
      } catch (err) {
        console.error("Error loading user:", err);
      } finally {
        this.$vs.loading.close();
      }
    },
    
    // NEW: Handles events fired from vanilla JS SandboxView
    handleShowRoleInspector(event) {
        const { nodes } = event.detail;
        if (nodes && nodes.length > 0) {
            this.$nextTick(() => {
                if (this.$refs.roleInspector) {
                    this.$refs.roleInspector.open(nodes);
                }
            });
        }
    },

    async fetchDataAndInit() {
      this.isInitializing = true;
      this.rootId = this.orgData._id;
      this.orgName = this.orgData.name || "Organization";
      this.primaryColor = this.orgData.primaryThemeColor || '#48A9C5';

      try {
          const API_BASE_URL = "https://api.getboarded.tech";
          const token = localStorage.getItem('accessToken') || this.$store.state.bearerToken;
          
          let candidateList = [];
          try {
              const cRes = await fetch(`${API_BASE_URL}/users?organizationId=${this.rootId}&$limit=500`, {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              const cData = await cRes.json();
              candidateList = cData.data || cData || [];
          } catch (e) {
              console.warn("Could not fetch candidate list", e);
          }

          const res = await this.$store.dispatch("kvdr/findKVDR", this.rootId);
          let rawData = [];

          if (!res || !res.data || !res.data.length) {
              if (!this.userId) throw new Error("Missing User ID");
              const newKvdr = await this.$store.dispatch("kvdr/createKVDR", { userId: this.userId, orgId: this.rootId });
              this.kvdrId = newKvdr._id;
              rawData = newKvdr.kvdrRoles || [];
          } else {
              this.kvdrId = res.data[0]._id;
              rawData = res.data[0].kvdrRoles || [];
          }

          let totalHeadcount = 0;
          const formattedData = rawData.map(node => {
              let profileIds = node.kvdrRoleProfiles || node.profiles || [];
              
              const enrichedProfiles = profileIds.map(p => {
                  const pObj = typeof p === 'object' ? p : { id: p };
                  const uid = String(pObj.userId || pObj.user_id || pObj._id || pObj.id);
                  const foundCandidate = candidateList.find(c => String(c.id) === uid || String(c._id) === uid || String(c.user_id) === uid) || {};
                  
                  return { ...pObj, ...foundCandidate, id: uid };
              }).filter(p => p.id && p.id !== 'undefined');

              totalHeadcount += enrichedProfiles.length;

              return {
                  ...node,
                  candidateCount: enrichedProfiles.length,
                  profiles: enrichedProfiles, 
                  displaySettings: node.displaySettings || { cc: this.primaryColor, tc: '#fff', bc: '#e5e7eb' }
              };
          });

          const rootDataObj = {
              selfId: this.rootId, 
              title: this.orgName, 
              parentId: null,
              roleSummary: "Organization Design Structure", 
              candidateCount: totalHeadcount, 
              profiles: [], 
              isRoot: true,
              displaySettings: { cc: this.primaryColor, tc: '#fff', bc: '#e5e7eb' }
          };

          this.graphData = [rootDataObj, ...formattedData];

          if (this.sandboxInstance) this.sandboxInstance.destroy(); 

          this.sandboxInstance = new SandboxView({
            container: 'role-design-sandbox-container',
            data: this.graphData,
            rootId: this.rootId,
            displaySettings: { cc: this.primaryColor, tc: '#fff', bc: '#e5e7eb' },
            actions: {
                onUpdate: async (nodeId, newData) => {
                    try {
                        await app.service('kvdr-role').patch(null, newData, { query: { selfId: nodeId } });
                        this.$vs.notify({ title: 'Success', text: 'Role updated', color: 'success' });
                    } catch (err) { console.error(err); }
                },

              onDelete: async (nodeId) => {
                try {
                  await app.service('kvdr-role').remove(null, { query: { selfId: nodeId } });
                  
                  const idx = this.graphData.findIndex(n => n.selfId === nodeId);
                  if(idx > -1) this.graphData.splice(idx, 1);
                  window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.graphData } }));
                  
                  this.$vs.notify({ title: 'Success', text: 'Role deleted successfully', color: 'success' });
                } catch (err) { console.error("Delete failed", err); }
              },

              onAdd: async (parentId, popupPayload) => {
                try {
                  const newNodeId = Utils.randomString(24);
                  const validParentId = (parentId === this.rootId) ? "0" : parentId;

                  const apiData = {
                      title: popupPayload.title || "New Role", 
                      roleSummary: popupPayload.roleSummary || "Role description...",
                      skills: popupPayload.skills || [],
                      softSkills: popupPayload.softSkills || [],
                      status: 'published',
                      parentId: validParentId,
                      selfId: newNodeId, 
                      kvdrId: this.kvdrId,
                      displaySettings: { cc: this.primaryColor }
                  };

                  await app.service('kvdr-role').create(apiData);
                  
                  this.graphData.push(apiData);
                  window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.graphData } }));
                  
                  this.$vs.notify({ title: 'Success', text: 'Role Created successfully', color: 'success' });
                } catch (err) { 
                  console.error("Create failed", err);
                  this.$vs.notify({ title: 'Error', text: 'Could not create role.', color: 'danger' });
                }
              }
            }
          });
          
          this.isInitializing = false;
          
      } catch (error) {
          console.error("CRITICAL ERROR during initialization:", error);
          this.isInitializing = false;
      }
    }
  }
}
</script>

<style scoped>
.role-design-page {
  height: calc(100vh - 65px); 
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa; 
  overflow: hidden; 
  margin: 0;
  padding: 0;
}

.canvas-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

#role-design-sandbox-container {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}
</style>